// FILE: src/pages/OnboardingPage.jsx
// Getting started (MTO 12.4–12.6, plan WP-6): before the first lesson the
// student completes, in order, the tutorial, the Code of Conduct and the
// syllabus. The server enforces the order and records each step with its
// document version (ONBOARDING_ORDER / ONBOARDING_REQUIRED); this page shows
// where the student is and lets them finish. Address and consent come first
// (StudentCompleteInfoPage, shown by MainLayout).
import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchOnboarding } from "../features/onboardingSlice";
import { acknowledgeDocument, completeTutorial } from "../services/courseInfo";
import { ErrorState, LoadingState, PageHeader } from "../components/ui";
import SimpleText from "../components/help/SimpleText";
import { DocumentBody } from "./info/LegalDocumentPage";
import { errorMessage } from "../i18n/errors";

const STEPS = [
  { id: "profile", label: "Your details" },
  { id: "tutorial", label: "Tutorial" },
  { id: "codeOfConduct", label: "Code of Conduct" },
  { id: "syllabus", label: "Syllabus" }
];

function StepCard({ title, intro, children, confirmLabel, onConfirm, busy, error }) {
  const [checked, setChecked] = useState(false);
  return (
    <Card>
      <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
        <Typography variant="h2" sx={{ mb: 1 }}>
          {title}
        </Typography>
        {intro && <Typography sx={{ color: "text.secondary", mb: 3 }}>{intro}</Typography>}
        <Box
          tabIndex={0}
          role="region"
          aria-label={title}
          sx={{
            maxHeight: { xs: "none", md: 520 },
            overflowY: { md: "auto" },
            p: { xs: 0, md: 3 },
            border: { md: "1px solid" },
            borderColor: { md: "divider" },
            borderRadius: 3,
            bgcolor: { md: "background.default" }
          }}
        >
          {children}
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} role="alert">
            {errorMessage(error)}
          </Alert>
        )}
        <FormControlLabel
          sx={{ mt: 3, alignItems: "flex-start", "& .MuiCheckbox-root": { mt: -0.75 } }}
          control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
          label={confirmLabel}
        />
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" size="large" disabled={!checked || busy} onClick={onConfirm}>
            {busy ? <CircularProgress size={22} color="inherit" aria-label="Saving" /> : "Continue"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

function Tutorial({ tutorial }) {
  return (
    <Stack spacing={3}>
      {tutorial?.videoUrl && (
        <Box>
          <Box
            component="video"
            controls
            preload="metadata"
            src={tutorial.videoUrl}
            sx={{ width: "100%", borderRadius: 3, bgcolor: "#000" }}
            aria-label="Course tutorial video"
          >
            {tutorial.captionsUrl && <track kind="captions" src={tutorial.captionsUrl} srcLang="en" label="English" default />}
          </Box>
          <Typography sx={{ color: "text.secondary", mt: 1 }}>
            The video has captions. The same information is written out below.
          </Typography>
        </Box>
      )}
      {(tutorial?.sections || []).map((s, i) => (
        <Box component="section" key={`${s.heading}-${i}`}>
          <Typography component="h3" variant="h3" sx={{ mb: 1 }}>
            {s.heading}
          </Typography>
          <SimpleText text={s.body} headingLevel={4} />
        </Box>
      ))}
    </Stack>
  );
}

export default function OnboardingPage() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up("md"));
  const { data, loading, error } = useSelector((s) => s.onboarding);
  const [busy, setBusy] = useState(false);
  const [stepError, setStepError] = useState(null);

  useEffect(() => {
    if (!data && !loading) dispatch(fetchOnboarding());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data && error) {
    return (
      <>
        <PageHeader title="Getting started" />
        <ErrorState error={error} onRetry={() => dispatch(fetchOnboarding())} />
      </>
    );
  }
  if (!data) return <LoadingState variant="page" label="Loading your first steps…" />;

  const done = Object.fromEntries((data.steps || []).map((s) => [s.id, s.done]));
  const activeIndex = STEPS.findIndex((s) => !done[s.id]);
  const active = activeIndex === -1 ? null : STEPS[activeIndex].id;

  async function run(fn) {
    setBusy(true);
    setStepError(null);
    try {
      await fn();
      await dispatch(fetchOnboarding());
      window.scrollTo({ top: 0 });
    } catch (err) {
      setStepError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box>
      <PageHeader
        title="Getting started"
        description="A few short steps before your first lesson. Ontario's rules for online driver education require them, and they explain how the course works."
      />

      <Stepper
        activeStep={activeIndex === -1 ? STEPS.length : activeIndex}
        orientation={wide ? "horizontal" : "vertical"}
        alternativeLabel={wide}
        sx={{ mb: 4 }}
        aria-label="Getting started steps"
      >
        {STEPS.map((s) => (
          <Step key={s.id} completed={!!done[s.id]}>
            <StepLabel optional={done[s.id] ? <Typography sx={{ color: "text.secondary" }}>Done</Typography> : null}>
              {s.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {active === null && (
        <Card sx={{ bgcolor: "zun.primaryTint", borderColor: "transparent" }} data-testid="onboarding-complete">
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1 }}>
              <CheckCircleOutlineIcon aria-hidden sx={{ color: "primary.main" }} />
              <Typography variant="h2">You're ready to start</Typography>
            </Stack>
            <Typography sx={{ mb: 2 }}>
              You can re-read the tutorial, Code of Conduct and syllabus any time from the Course information page.
            </Typography>
            <Button component={RouterLink} to="/student" variant="contained" size="large">
              Go to my first lesson
            </Button>
          </CardContent>
        </Card>
      )}

      {active === "tutorial" && (
        <StepCard
          key="tutorial"
          title="Tutorial: how the course works"
          intro="How to move through lessons, the time rules, identity checks, getting help and how you pass."
          confirmLabel="I have watched or read the tutorial."
          busy={busy}
          error={stepError}
          onConfirm={() => run(() => completeTutorial())}
        >
          <Tutorial tutorial={data.tutorial} />
        </StepCard>
      )}

      {active === "codeOfConduct" && data.documents?.codeOfConduct && (
        <StepCard
          key="coc"
          title={data.documents.codeOfConduct.title || "Code of Conduct"}
          intro={`Version ${data.documents.codeOfConduct.version}. Please read it all.`}
          confirmLabel="I have read the Code of Conduct and agree to follow it."
          busy={busy}
          error={stepError}
          onConfirm={() => run(() => acknowledgeDocument("codeOfConduct", data.documents.codeOfConduct.version))}
        >
          <DocumentBody doc={data.documents.codeOfConduct} headingLevel={3} />
        </StepCard>
      )}

      {active === "syllabus" && data.documents?.syllabus && (
        <StepCard
          key="syllabus"
          title={data.documents.syllabus.title || "Course syllabus"}
          intro={`Version ${data.documents.syllabus.version}. What you'll learn, how you're assessed and how the course is organised.`}
          confirmLabel="I have read the syllabus."
          busy={busy}
          error={stepError}
          onConfirm={() => run(() => acknowledgeDocument("syllabus", data.documents.syllabus.version))}
        >
          <DocumentBody doc={data.documents.syllabus} headingLevel={3} />
        </StepCard>
      )}
    </Box>
  );
}
